import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/upr-api";

const TEMPLATE_LABELS = {
  welcome: {
    title: "Newsletter Welcome",
    hint: "Sent to a subscriber right after they enter their email in the footer signup.",
  },
  notify_me: {
    title: "Notify Me Confirmation",
    hint: "Sent to a customer the moment they click \"Notify me\" on a sold-out item — confirms they're on the waiting list.",
  },
  restock: {
    title: "Back-In-Stock Blast",
    hint: "Sent to the whole waiting list when you click \"Restock N\" from the Merch admin.",
  },
};

const ORDER = ["welcome", "notify_me", "restock"];

export default function EmailTemplatesAdmin() {
  const [templates, setTemplates] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [placeholders, setPlaceholders] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data.templates);
      setDefaults(data.defaults || {});
      setPlaceholders(data.placeholders || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load templates.");
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (key, field, value) => {
    setTemplates((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));
  };

  const resetOne = (key) => {
    if (!window.confirm(`Reset "${TEMPLATE_LABELS[key].title}" to the default template? Your current text will be lost.`)) return;
    setTemplates((t) => ({ ...t, [key]: { ...defaults[key] } }));
    toast.success("Reverted — remember to hit Save.");
  };

  const sendTest = async (key) => {
    try {
      const { data } = await api.post(`/email-templates/test/${key}`);
      if (data.skipped) {
        toast(`Email sending disabled locally — would have sent to ${data.sent_to}`, { description: data.reason });
      } else {
        toast.success(`Test sent to ${data.sent_to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed.");
    }
  };

  const save = async () => {
    if (!templates) return;
    setSaving(true);
    try {
      const { data } = await api.put("/email-templates", templates);
      setTemplates(data.templates);
      toast.success("Templates saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!templates) {
    return <div className="text-sm" style={{ color: "var(--upr-muted)" }} data-testid="email-templates-loading">Loading…</div>;
  }

  return (
    <section data-testid="email-templates-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="upr-eyebrow mb-1">Email Templates</div>
          <h2 className="font-heading font-black text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em" }}>
            Customize the outbound emails
          </h2>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--upr-muted)" }}>
            Edit the subject line and body text for each customer email. Use placeholders
            (e.g. <code style={{ background: "var(--upr-surface)", padding: "2px 6px" }}>[ItemName]</code>) to keep it personal —
            they&apos;re swapped for real values when the email is sent.
          </p>
        </div>
        <button className="upr-btn" onClick={save} disabled={saving} data-testid="email-templates-save">
          <Save size={14} /> {saving ? "Saving…" : "Save all templates"}
        </button>
      </div>

      <div className="space-y-10">
        {ORDER.map((key) => (
          <TemplateCard
            key={key}
            tplKey={key}
            title={TEMPLATE_LABELS[key].title}
            hint={TEMPLATE_LABELS[key].hint}
            value={templates[key]}
            placeholders={placeholders[key] || []}
            onChange={(field, value) => patch(key, field, value)}
            onReset={() => resetOne(key)}
            onSendTest={() => sendTest(key)}
          />
        ))}
      </div>

      <div className="mt-10 pt-6 flex justify-end border-t" style={{ borderColor: "var(--upr-border)" }}>
        <button className="upr-btn" onClick={save} disabled={saving} data-testid="email-templates-save-bottom">
          <Save size={14} /> {saving ? "Saving…" : "Save all templates"}
        </button>
      </div>
    </section>
  );
}

function TemplateCard({ tplKey, title, hint, value, placeholders, onChange, onReset, onSendTest }) {
  return (
    <div className="p-6 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }} data-testid={`tpl-${tplKey}`}>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="upr-eyebrow mb-1">Template</div>
          <h3 className="font-heading font-extrabold text-xl" style={{ letterSpacing: "-0.01em" }}>{title}</h3>
          <p className="text-sm mt-1 max-w-xl" style={{ color: "var(--upr-muted)" }}>{hint}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={onSendTest}
            className="upr-btn upr-btn-ghost"
            data-testid={`tpl-${tplKey}-send-test`}
            title="Save current changes first — the test uses whatever is currently saved on the server."
          >
            <Send size={14} /> Send test to me
          </button>
          <button
            type="button"
            onClick={onReset}
            className="upr-btn upr-btn-ghost"
            data-testid={`tpl-${tplKey}-reset`}
          >
            <RotateCcw size={14} /> Reset to default
          </button>
        </div>
      </div>

      <label className="block mb-4">
        <span className="upr-eyebrow block mb-2">Subject line</span>
        <input
          type="text"
          value={value.subject}
          onChange={(e) => onChange("subject", e.target.value)}
          className="w-full p-3 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] transition-colors"
          style={{ borderColor: "var(--upr-border)" }}
          data-testid={`tpl-${tplKey}-subject`}
        />
      </label>

      <label className="block">
        <span className="upr-eyebrow block mb-2">Body</span>
        <textarea
          value={value.body}
          onChange={(e) => onChange("body", e.target.value)}
          rows={10}
          className="w-full p-3 bg-transparent border font-mono text-sm focus:outline-none focus:border-[color:var(--upr-primary)] transition-colors"
          style={{ borderColor: "var(--upr-border)", lineHeight: 1.55 }}
          data-testid={`tpl-${tplKey}-body`}
        />
      </label>
      <p className="text-xs mt-2" style={{ color: "var(--upr-muted)" }}>
        Blank line = new paragraph. A single line-break just wraps.
      </p>

      {placeholders.length > 0 && (
        <div className="mt-4">
          <div className="upr-eyebrow mb-2" style={{ fontSize: "0.62rem" }}>Available placeholders — click to copy</div>
          <div className="flex flex-wrap gap-2">
            {placeholders.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(p).then(
                    () => toast.success(`Copied ${p}`),
                    () => toast.error("Could not copy"),
                  );
                }}
                className="px-2 py-1 text-xs font-mono border transition-colors hover:border-[color:var(--upr-primary)] hover:text-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)", background: "var(--upr-bg)" }}
                data-testid={`tpl-${tplKey}-ph-${p.replace(/[[\]]/g, "").toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
